import React, { useState, useRef, useEffect } from "react";
import downArrowSvg from "assets/img/arrow-down.svg";
import CustomCheckbox from "./CustomCheckbox";

export interface MultiSelectOption {
  id: number | string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: (number | string)[] | number | string;
  onChange: (value: (number | string)[] | number | string) => void;
  multiSelect?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  hideHeader?: boolean;
  headerLabel?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  multiSelect = true,
  isDisabled = false,
  placeholder = "Select",
  hideHeader = true,
  headerLabel = "Select",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState<
    Set<number | string>
  >(new Set());

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync external value
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedItems(new Set(value));
    } else if (value !== undefined && value !== null) {
      setSelectedItems(new Set([value]));
    } else {
      setSelectedItems(new Set());
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id: number | string) => {
    if (multiSelect) {
      const updated = new Set(selectedItems);
      if (updated.has(id)) updated.delete(id);
      else updated.add(id);

      setSelectedItems(updated);
      onChange(Array.from(updated));
    } else {
      setSelectedItems(new Set([id]));
      onChange(id);
      setIsOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === options.length) {
      setSelectedItems(new Set());
      onChange(multiSelect ? [] : "");
    } else {
      const allIds = options.map((o) => o.id);
      setSelectedItems(new Set(allIds));
      onChange(multiSelect ? allIds : allIds[0]);
    }
  };

  const getDisplayText = () => {
    if (isDisabled) return placeholder;
    if (selectedItems.size === 0) return placeholder;
    if (selectedItems.size === options.length)
      return "All Selected";
    if (selectedItems.size === 1) {
      const selected = options.find(
        (o) => o.id === Array.from(selectedItems)[0]
      );
      return selected ? selected.label : placeholder;
    }
    return `${selectedItems.size} Selected`;
  };

  const filteredOptions = searchTerm
    ? options.filter((o) =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  return (
    <div style={styles.inputGroup}>
      {!hideHeader && (
        <div style={styles.inputLabel}>{headerLabel}</div>
      )}

      <div style={styles.selectWrapper} ref={dropdownRef}>
        <div
          style={{
            ...styles.selectInput,
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
          onClick={() =>
            !isDisabled && setIsOpen(!isOpen)
          }
        >
          <span>{getDisplayText()}</span>
          <img src={downArrowSvg} alt="" />
        </div>

        {isOpen && !isDisabled && (
          <div style={styles.dropdown}>
            <div style={styles.searchWrapper}>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                style={styles.searchInput}
              />
            </div>

            <div style={styles.optionsList}>
              {multiSelect && (
                <div style={styles.option}>
                  <CustomCheckbox
                    checked={
                      selectedItems.size === options.length
                    }
                    onChange={handleSelectAll}
                    label="Select All"
                  />
                </div>
              )}

              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  style={styles.option}
                  onClick={() =>
                    handleSelect(option.id)
                  }
                >
                  {multiSelect ? (
                    <CustomCheckbox
                      checked={selectedItems.has(
                        option.id
                      )}
                      onChange={() =>
                        handleSelect(option.id)
                      }
                      label={option.label}
                    />
                  ) : (
                    <span>{option.label}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: 400,
    marginBottom: 8,
  },
  selectWrapper: {
    position: "relative",
  },
  selectInput: {
    padding: "10px 14px",
    border: "1px solid #ddd",
    borderRadius: 16,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    height: 44,
    fontSize: 12,
    fontWeight: 400,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    zIndex: 1000,
    maxHeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  searchWrapper: {
    padding: 10,
    borderBottom: "1px solid #eee",
  },
  searchInput: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 4,
    border: "1px solid #ddd",
  },
  optionsList: {
    overflowY: "auto",
    maxHeight: 300,
  },
  option: {
    padding: "10px 15px",
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
    fontSize: 12,
    fontWeight: 400,
  },
};

export default MultiSelectDropdown;
